import { WarningCircleIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import type {
  WorkbookDefinition,
  WorkbookDiagnostic,
  WorkbookDiagnosticCode,
  WorkbookSheet,
} from '@erp/shared-types';
import { diagnosticPartName } from '@erp/workbooks';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

/** A Diagnostic in the user's language: where it is, and what is wrong. */
export interface DiagnosticText {
  /** The Item's name. */
  item: string;
  /** The part of a Worksheet it points at, or null for the whole Item. */
  part: string | null;
  message: string;
}

/** Codes that are about a column's name rather than its Formula. */
const NAME_CODES: ReadonlySet<WorkbookDiagnosticCode> = new Set([
  'duplicate-column',
  'column-case-mismatch',
  'reserved-name',
]);

/**
 * A Diagnostic that says an Item is unfinished rather than wrong, such as a
 * new pivot with no fields yet. It still blocks a Run, but is shown as a
 * next step, never as an error.
 */
export function isSetupDiagnostic(diagnostic: WorkbookDiagnostic): boolean {
  return (
    diagnostic.code === 'empty-pivot' ||
    diagnostic.code === 'freeform-table-range'
  );
}

export function isNameDiagnostic(diagnostic: WorkbookDiagnostic): boolean {
  return NAME_CODES.has(diagnostic.code);
}

/** Translates Diagnostics by code; the English `message` stays for the agent and logs. */
export function useDiagnosticText(definition: WorkbookDefinition) {
  const gt = useGT();
  const messages: Record<WorkbookDiagnosticCode, string> = {
    'formula-syntax': gt('This formula cannot be read.', {
      context: 'workbook diagnostic',
    }),
    'unknown-function': gt(
      'This formula uses a function that does not exist.',
      {
        context: 'workbook diagnostic',
      },
    ),
    'qualified-reference': gt(
      "This formula refers to another input's column. Reference this worksheet's input, or add a lookup.",
      { context: 'workbook diagnostic' },
    ),
    'unknown-column': gt(
      'This formula refers to a column that does not exist.',
      {
        context: 'workbook diagnostic',
      },
    ),
    'unknown-table': gt(
      'This formula refers to a source or worksheet that does not exist, or to a name more than one of them has.',
      { context: 'workbook diagnostic' },
    ),
    'table-reference': gt(
      "Another table's columns can only be read by a lookup or a conditional aggregate: XLOOKUP's lookup and return arrays, such as Prices[SKU], VLOOKUP's table, such as Prices, or the ranges of SUMIFS and COUNTIFS, all columns of one table.",
      { context: 'workbook diagnostic' },
    ),
    'lookup-mode': gt(
      "XLOOKUP's match_mode and search_mode are not supported yet. Remove them to find an exact match.",
      { context: 'workbook diagnostic' },
    ),
    'approximate-match': gt(
      'Spreadsheets use approximate match when VLOOKUP has no FALSE as its last input, and only exact match is supported. Add FALSE, as in VLOOKUP([@SKU], Prices, 2, FALSE).',
      { context: 'workbook diagnostic' },
    ),
    'lookup-column-index': gt(
      "VLOOKUP's column number must be a whole number from 1 to the number of columns in its table.",
      { context: 'workbook diagnostic' },
    ),
    'argument-type': gt(
      "One of this function's inputs can never be the type it needs, such as a Number where a Date belongs.",
      { context: 'workbook diagnostic' },
    ),
    'text-format': gt(
      'TEXT needs a format written in the formula that it supports, such as "0.00", "#,##0", "0%", "yyyy-mm-dd" or "mmm yyyy".',
      { context: 'workbook diagnostic' },
    ),
    'aggregate-criteria': gt(
      'SUMIFS and COUNTIFS need a value to match after each criteria range, and match only equal values: comparisons and wildcards are not supported.',
      { context: 'workbook diagnostic' },
    ),
    'column-cycle': gt(
      'These formulas refer to each other (a circular reference). A column can use its own input value, but not a column that uses it.',
      { context: 'workbook diagnostic' },
    ),
    'duplicate-column': gt(
      'Output column names must be unique, ignoring case.',
      { context: 'workbook diagnostic' },
    ),
    'column-case-mismatch': gt(
      'Match the casing of the existing input column, or use a distinct name.',
      { context: 'workbook diagnostic' },
    ),
    'reserved-name': gt('Names beginning with __wb_ are reserved.', {
      context: 'workbook diagnostic',
    }),
    'missing-input': gt("This worksheet's input no longer exists.", {
      context: 'workbook diagnostic',
    }),
    'sheet-cycle': gt(
      "These worksheets use each other as input (a circular reference). A worksheet can't use itself, or a worksheet that uses it.",
      { context: 'workbook diagnostic' },
    ),
    'missing-lookup-source': gt("This lookup's source no longer exists.", {
      context: 'workbook diagnostic',
    }),
    'unknown-lookup-column': gt(
      'This lookup refers to a column that does not exist.',
      { context: 'workbook diagnostic' },
    ),
    'lookup-output-collision': gt(
      "This lookup's result needs a new column name.",
      { context: 'workbook diagnostic' },
    ),
    'lookup-type-mismatch': gt(
      "The lookup column and the column it matches must be the same type (for example, both Text). Change one column's type in an earlier worksheet.",
      { context: 'workbook diagnostic' },
    ),
    'unknown-field': gt('This refers to a column that does not exist.', {
      context: 'workbook diagnostic',
    }),
    'empty-value-list': gt('Choose at least one value for this filter.', {
      context: 'workbook diagnostic',
    }),
    'validation-range': gt(
      'A range checks a Number, Date or Date and time column, and needs a smallest value, a largest value, or both.',
      { context: 'workbook diagnostic' },
    ),
    'validation-pattern': gt(
      "This pattern isn't a regular expression the workbook can check. Patterns can't look ahead, look behind or refer back to a group.",
      { context: 'workbook diagnostic' },
    ),
    'validation-not-boolean': gt(
      'A validation formula must give TRUE or FALSE, such as [@Qty] > 0.',
      { context: 'workbook diagnostic' },
    ),
    'validation-sheet': gt(
      "This validation compares with a worksheet or column that doesn't exist.",
      { context: 'workbook diagnostic' },
    ),
    'empty-pivot': gt('Add a row field or a value to this pivot.', {
      context: 'workbook diagnostic',
    }),
    'pivot-date-group': gt(
      'Only a Date or Text field can be grouped by year, quarter, month or day.',
      { context: 'workbook diagnostic' },
    ),
    'pivot-value-type': gt(
      "This field isn't a Number, so it can only be counted (COUNTA or COUNTUNIQUE).",
      { context: 'workbook diagnostic' },
    ),
    'pivot-sort-value': gt(
      'This field sorts by a value the pivot no longer has.',
      { context: 'workbook diagnostic' },
    ),
    'pivot-column-keys': gt(
      'The pinned columns no longer match the column fields. Pin the columns again.',
      { context: 'workbook diagnostic' },
    ),
    'cell-cycle': gt(
      'This formula refers to itself through other cells (a circular reference).',
      { context: 'workbook diagnostic' },
    ),
    'freeform-table-header': gt(
      'Each header cell of the result table names its column: type text, unique ignoring case, not a formula.',
      { context: 'workbook diagnostic' },
    ),
    'freeform-table-range': gt(
      'Choose the cells its result table reads, under Result table.',
      { context: 'workbook diagnostic' },
    ),
    'freeform-missing-column': gt(
      'This formula refers to a column its source or worksheet no longer has (it was renamed or removed), so it gives #REF!. Point it at a column the table has.',
      { context: 'workbook diagnostic' },
    ),
  };
  const emptyAcceptedValues = gt('Choose at least one accepted value.', {
    context: 'workbook diagnostic on a validation',
  });
  const items = [...definition.sources, ...definition.sheets];
  function pivotPart(
    location: Extract<WorkbookDiagnostic['location'], { part: 'pivot' }>,
  ): string {
    const found = definition.sheets.find((item) => item.id === location.itemId);
    const fallback = String(location.index + 1);
    const context = 'workbook diagnostic location';
    const labels = {
      rows: (field: string) => gt('Row field {field}', { context, field }),
      columns: (field: string) =>
        gt('Column field {field}', { context, field }),
      values: (field: string) => gt('Value {field}', { context, field }),
      filters: (column: string) =>
        gt('Filter on {column}', { context, column }),
    };
    return labels[location.area](
      diagnosticPartName(found, location) ?? fallback,
    );
  }
  function part(location: WorkbookDiagnostic['location']): string | null {
    if (location.part === 'item') return null;
    if (location.part === 'pivot') return pivotPart(location);
    if (location.part === 'cell')
      return gt('Cell {cell}', {
        context: 'workbook diagnostic location',
        cell: location.cell,
      });
    const found = definition.sheets.find((item) => item.id === location.itemId);
    const position = String(location.index + 1);
    const context = 'workbook diagnostic location';
    if (location.part === 'validation')
      return gt('Validation on {column}', {
        context,
        column: diagnosticPartName(found, location) ?? position,
      });
    const name = diagnosticPartName(found, location) ?? position;
    const labels = {
      column: () => gt('Column {name}', { context, name }),
      lookup: () => gt('Lookup {position}', { context, position }),
      filter: () => gt('Filter on {column}', { context, column: name }),
      sort: () => gt('Sort by {column}', { context, column: name }),
    };
    return labels[location.part]();
  }
  return (diagnostic: WorkbookDiagnostic): DiagnosticText => ({
    item:
      items.find((item) => item.id === diagnostic.location.itemId)?.name ??
      definition.name,
    part: part(diagnostic.location),
    message:
      diagnostic.code === 'empty-value-list' &&
      diagnostic.location.part === 'validation'
        ? emptyAcceptedValues
        : messages[diagnostic.code],
  });
}

/** The Worksheet's Diagnostics, located; a column Diagnostic selects its column. */
export function WorksheetDiagnostics({
  sheet,
  definition,
  diagnostics,
  selectColumn,
}: {
  sheet: WorkbookSheet;
  definition: WorkbookDefinition;
  diagnostics: readonly WorkbookDiagnostic[];
  selectColumn: (index: number) => void;
}) {
  const text = useDiagnosticText(definition);
  if (!diagnostics.length) return null;
  return (
    <AlertContainer variant="destructive" role="status" className="py-2">
      <WarningCircleIcon />
      <AlertDescription>
        <p className="font-medium text-destructive">
          <T context="worksheet diagnostics heading">
            Fix these before running the workbook:
          </T>
        </p>
        <ul className="grid gap-1 text-xs">
          {diagnostics.map((diagnostic, index) => {
            const { part, message } = text(diagnostic);
            const { location } = diagnostic;
            const label = part ?? sheet.name;
            return (
              <li key={index} className="flex items-baseline gap-1">
                {location.part === 'column' ? (
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs text-destructive"
                    onClick={() => selectColumn(location.index)}
                  >
                    {label}
                  </Button>
                ) : (
                  <span className="font-medium">{label}</span>
                )}
                <span>{message}</span>
              </li>
            );
          })}
        </ul>
      </AlertDescription>
    </AlertContainer>
  );
}
